// GlobalSnackbar.tsx
import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

// Define the shape of the alert state
interface SnackbarState {
    open: boolean;
    message: string;
    severity: AlertColor;
}

interface SnackbarContextType {
    showSnackbar: (message: string, severity: AlertColor) => void;
    closeSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

// Custom hook to access the snackbar context
export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error("useSnackbar must be used within a SnackbarProvider");
    }
    return context;
};

// Snackbar provider component
export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [alertState, setAlertState] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success',
    });

    // Stable identity so consumers that put showSnackbar in useCallback/useEffect
    // deps do not re-fire indefinitely when a snackbar opens (e.g. on API error).
    const showSnackbar = useCallback((message: string, severity: AlertColor) => {
        // Auth expiry is handled by the API layer (redirect to /login).
        // Skip the toast so it does not flash before navigation.
        if (
            typeof message === 'string' &&
            message.toLowerCase().includes('invalid authentication')
        ) {
            return;
        }

        setAlertState({
            open: true,
            message,
            severity,
        });

        // Auto-hide after 4 seconds (if you want to trigger the close programmatically)
        setTimeout(() => {
            setAlertState((prevState) => ({
                ...prevState,
                open: false,
            }));
        }, 4000); // Change the duration as needed
    }, []);

    const closeSnackbar = useCallback(() => {
        setAlertState((prevState) => ({
            ...prevState,
            open: false,
        }));
    }, []);

    const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        closeSnackbar();
    };

    const contextValue = useMemo(
        () => ({ showSnackbar, closeSnackbar }),
        [showSnackbar, closeSnackbar],
    );

    return (
        <SnackbarContext.Provider value={contextValue}>
            {children}
            <Snackbar
                open={alertState.open}
                autoHideDuration={4000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleClose} severity={alertState.severity} sx={{ width: '100%' }}>
                    {alertState.message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};
